/**
 * Course Service - Abstracted data persistence layer
 * Calls Rails API for all course operations
 */

import { createCourse, validateCourse, fromRailsFormat, toRailsFormat } from '../models/Course.js'
import { apiRequest } from '../config/api.js'
import { handleApiError } from './errorHandler.js'

/**
 * Get courses with pagination
 * @param {Object} options - Pagination options
 * @param {number} options.page - Page number (default: 1)
 * @param {number} options.perPage - Items per page (default: 20)
 * @param {string} options.statusFilter - Status filter for admin: active, any, all_except_inactive, in_progress, needs_review, info_missing, inactive (default: all_except_inactive for admin list)
 * @param {string} options.search - Optional search query (admin: matches name, description, instructor, provider name)
 * @param {string} options.courseType - Optional activity/course type filter (admin)
 * @param {string} options.providerId - Optional provider ID filter (admin)
 * @returns {Promise<Object>} Object with data array and pagination info
 */
export async function getCoursesPaginated({ page = 1, perPage = 20, statusFilter = 'all_except_inactive', search = '', courseType = '', providerId = '' } = {}) {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString()
    })
    if (statusFilter) {
      params.append('status_filter', statusFilter)
    }
    if (search && search.trim()) {
      params.append('search', search.trim())
    }
    if (courseType && courseType.trim()) {
      params.append('course_type', courseType.trim())
    }
    if (providerId && providerId.trim()) {
      params.append('provider_id', providerId.trim())
    }

    const data = await apiRequest(`/courses?${params.toString()}`, {
      method: 'GET'
    })
    
    // Handle new paginated format
    if (data.data && data.pagination) {
      return {
        data: data.data.map(course => fromRailsFormat(course)),
        pagination: data.pagination
      }
    }
    
    // Fallback for old format (backward compatibility)
    const courses = Array.isArray(data) ? data : (data.courses || [])
    return {
      data: courses.map(course => fromRailsFormat(course)),
      pagination: {
        page: 1,
        per_page: courses.length,
        total: courses.length,
        total_pages: 1
      }
    }
  } catch (error) {
    handleApiError(error)
    throw error
  }
}

/**
 * Save a new course
 * @param {Object} courseData - Course object to save (in camelCase)
 * @returns {Promise<Object>} Saved course object with ID
 */
export async function saveCourse(courseData) {
  // If provider_id is provided, skip validation that requires providerType/providerName
  // The backend will validate the provider_id exists
  let courseToValidate = courseData
  if (!courseData.provider_id && !courseData.providerId) {
    courseToValidate = createCourse(courseData)
    const validation = validateCourse(courseToValidate)
    if (!validation.isValid) {
      throw new Error(`Invalid course data: ${validation.errors.join(', ')}`)
    }
  }

  // Convert to Rails format
  const railsData = toRailsFormat(courseToValidate)
  // Remove id from create payload (will be generated by backend)
  delete railsData.id

  try {
    const data = await apiRequest('/courses', {
      method: 'POST',
      includeAuth: true,
      body: JSON.stringify({ course: railsData })
    })
    return fromRailsFormat(data)
  } catch (error) {
    handleApiError(error)
    throw error
  }
}

/**
 * Get a single course by ID
 * @param {string} courseId - Course ID
 * @returns {Promise<Object|null>} Course object or null if not found
 */
export async function getCourseById(courseId) {
  try {
    const data = await apiRequest(`/courses/${courseId}`, {
      method: 'GET'
    })
    return fromRailsFormat(data)
  } catch (error) {
    if (error.status === 404) {
      return null
    }
    handleApiError(error)
    throw error
  }
}

/**
 * Update an existing course
 * @param {string} courseId - Course ID
 * @param {Object} courseData - Updated course data (in camelCase)
 * @returns {Promise<Object>} Updated course object
 */
export async function updateCourse(courseId, courseData) {
  // If provider_id is provided, skip validation that requires providerType/providerName
  // The backend will validate the provider_id exists
  let courseToValidate = { ...courseData, id: courseId }
  if (!courseData.provider_id && !courseData.providerId) {
    courseToValidate = createCourse(courseToValidate)
    const validation = validateCourse(courseToValidate)
    if (!validation.isValid) {
      throw new Error(`Invalid course data: ${validation.errors.join(', ')}`)
    }
  }

  // Convert to Rails format
  const railsData = toRailsFormat(courseToValidate)
  // Remove id from update payload (sent in URL)
  delete railsData.id

  try {
    const data = await apiRequest(`/courses/${courseId}`, {
      method: 'PUT',
      includeAuth: true,
      body: JSON.stringify({ course: railsData })
    })
    return fromRailsFormat(data)
  } catch (error) {
    handleApiError(error)
    throw error
  }
}

/**
 * Update only a course's status (admin only)
 * @param {string} courseId - Course ID
 * @param {string} status - New status: needs_review, info_missing, inactive, active
 * @returns {Promise<Object>} Updated course object
 */
export async function updateCourseStatus(courseId, status) {
  try {
    const data = await apiRequest(`/courses/${courseId}/status`, {
      method: 'PATCH',
      includeAuth: true,
      body: JSON.stringify({ status })
    })
    return fromRailsFormat(data)
  } catch (error) {
    handleApiError(error)
    throw error
  }
}

/**
 * Delete a course
 * @param {string} courseId - Course ID
 * @returns {Promise<boolean>} True if deleted successfully
 */
export async function deleteCourse(courseId) {
  try {
    await apiRequest(`/courses/${courseId}`, {
      method: 'DELETE',
      includeAuth: true
    })
    return true
  } catch (error) {
    if (error.status === 404) {
      return false
    }
    handleApiError(error)
    throw error
  }
}

/**
 * Get the three most recently reviewed courses
 * @returns {Promise<Array>} Array of course objects
 */
export async function getRecentlyReviewedCourses() {
  try {
    const data = await apiRequest('/courses/recently_reviewed', {
      method: 'GET'
    })
    
    const courses = Array.isArray(data) ? data : (data.courses || [])
    return courses.map(course => fromRailsFormat(course))
  } catch (error) {
    handleApiError(error)
    throw error
  }
}

/**
 * Get the three most recently added courses
 * @returns {Promise<Array>} Array of course objects
 */
export async function getRecentlyAddedCourses() {
  try {
    const data = await apiRequest('/courses/recently_added', {
      method: 'GET'
    })
    
    const courses = Array.isArray(data) ? data : (data.courses || [])
    return courses.map(course => fromRailsFormat(course))
  } catch (error) {
    handleApiError(error)
    throw error
  }
}

/**
 * Import courses from CSV file (admin only)
 * @param {File} file - CSV file to import
 * @returns {Promise<Object>} Import result with success/failure counts and errors
 */
export async function importCoursesFromCSV(file) {
  try {
    const formData = new FormData()
    formData.append('file', file)

    const { getApiUrl } = await import('../config/api.js')
    const url = getApiUrl('/courses/import')

    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include', // Include cookies for session-based auth
      body: formData
    })

    const contentType = response.headers.get('content-type')
    const isJson = contentType && contentType.includes('application/json')
    
    let data = null
    if (isJson) {
      data = await response.json()
    } else {
      data = await response.text()
    }

    if (!response.ok) {
      const error = new Error(data.message || data.error || `HTTP ${response.status}: ${response.statusText}`)
      error.status = response.status
      error.data = data
      handleApiError(error)
      throw error
    }

    return data
  } catch (error) {
    handleApiError(error)
    throw error
  }
}

